import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Grid,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { iepaProd } from "../../constants/global";

import Papa from "papaparse";
import * as XLSX from "xlsx";

const Imex = () => {
  const [activeTab, setActiveTab] = useState(0);

  const [selectedTable, setSelectedTable] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);

  const [headers, setHeaders] = useState([]);
  const [selectedHeaders, setSelectedHeaders] = useState([]);
  const [fileData, setFileData] = useState([]);

  const [sourceName, setSourceName] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState("excel");
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
const [applications, setApplications] = useState([]);
const [applicationsLoading, setApplicationsLoading] = useState(false);
 

  useEffect(() => {
    const fetchSources = async () => {
      setSourcesLoading(true);
      try {

        const response = await fetch(
          `${iepaProd.iepaAdapterSrvAPI}/adapters/source/active/org/1`
        );
        const data = await response.json();

        // ✅ if API returns array of strings
        setSources(data);

        // ❗ if API returns objects, use instead:
        // setSources(data.map(item => item.sourceName));
      } catch (error) {
        console.error("Failed to fetch sources", error);
      } finally {
        setSourcesLoading(false);
      }
    };

    fetchSources();
  }, []);

  useEffect(() => {
  if (!sourceName) {
    setApplications([]);
    return;
  }

  const fetchApplications = async () => {
    setApplicationsLoading(true);
    try {
      const response = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/target/active/org/1`
      );
      const data = await response.json();

      // OPTIONAL: filter by sourceName if backend returns mixed data
      const filtered = data.filter(
        (item) => item.sourceName === sourceName || !item.sourceName
      );

      setApplications(filtered);
    } catch (error) {
      console.error("Failed to fetch applications", error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  fetchApplications();
}, [sourceName]);

  // 🔹 AUTO-SELECT APPLICATION WHEN SOURCE = CONFIGURATION
 useEffect(() => {
  if (sourceName === "Configuration") {
    setSelectedTable("Configuration");
  }
}, [sourceName]);
  // ---------------- FILE UPLOAD ----------------
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".csv")) {
      alert("Upload Excel or CSV only");
      return;
    }

    setUploadedFile(file);

    if (fileName.endsWith(".csv")) {
      Papa.parse(file, {
        header: true,
        complete: (result) => {
          setHeaders(result.meta.fields || []);
          setSelectedHeaders([]);
          setFileData(result.data || []);
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        const cols = Object.keys(rows[0] || {});
        setHeaders(cols);
        setSelectedHeaders([]);
        setFileData(rows);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // ---------------- FILTER DATA ----------------
  const getFilteredData = () =>
    fileData.map((row) => {
      const filtered = {};
      selectedHeaders.forEach((h) => (filtered[h] = row[h]));
      return filtered;
    });

  // ---------------- IMPORT ----------------
  const handleImport = async () => {
  if (!uploadedFile || !selectedTable || !sourceName) {
    alert("Missing required fields");
    return;
  }

  const fileName = uploadedFile.name.toLowerCase();
  const isCsv = fileName.endsWith(".csv");
  const isExcel = fileName.endsWith(".xlsx");

  if (!isCsv && !isExcel) {
    alert("Only CSV or Excel files are allowed");
    return;
  }

  const apiUrl = isCsv
    ? `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/upload/csv`
    : `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/upload/excel`;

  const formData = new FormData();
  formData.append("file", uploadedFile);
  formData.append("org_id", "1");               // hardcoded as per requirement
  formData.append("created_by", applications[0].createdBy);           // replace with logged-in user id
  formData.append("source", sourceName);        // Source Name
  formData.append("destination", selectedTable); // Target Application

  setLoading(true);

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Upload failed");
    }

    alert("✅ File uploaded and mapping initiated successfully");
  } catch (error) {
    console.error(error);
    alert("❌ Upload failed");
  } finally {
    setLoading(false);
  }
};

  // ---------------- EXPORT ----------------
  const handleExport = () => {
    if (!selectedTable || !exportType) return;

    const data = getFilteredData();

    if (exportType === "csv") {
      const csv = Papa.unparse(data);
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "export.csv";
      link.click();
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export");
    XLSX.writeFile(workbook, "export.xlsx");
  };

  // ---------------- UI ----------------
  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h3" mb={2} textAlign="center">
        ImEx
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        centered
      >
        <Tab label="Import" />
        <Tab label="Export" />
      </Tabs>

      {/* ================= IMPORT TAB ================= */}
      {activeTab === 0 && (
        <Paper sx={{ mt: 4, p: 4, backgroundColor: "white" }} elevation={3}>
          <Stack direction="row" justifyContent="center" mb={4}>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
            >
              {uploadedFile ? uploadedFile.name : "Upload File"}
              <input
                type="file"
                hidden
                accept=".csv,.xlsx"
                onChange={handleFileChange}
              />
            </Button>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Source Name</InputLabel>
                <Select
                  value={sourceName}
                  label="Source Name"
                  onChange={(e) => setSourceName(e.target.value)}
                >
                  {sources.map((s) => (
                    <MenuItem key={s.id} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
  <InputLabel>Select Application</InputLabel>
  <Select
    value={selectedTable}
    label="Select Application"
    onChange={(e) => setSelectedTable(e.target.value)}
    disabled={
      sourceName === "Configuration" || applicationsLoading
    }
  >
    {applicationsLoading && (
      <MenuItem disabled>
        <CircularProgress size={20} />
      </MenuItem>
    )}

    {applications.map((app) => (
      <MenuItem key={app.id} value={app.module}>
        {app.module}
      </MenuItem>
    ))}
  </Select>
</FormControl>
            </Grid>
          </Grid>

          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              color="success"
              startIcon={<CloudUploadIcon />}
              onClick={handleImport}
              disabled={loading}
            >
              {loading ? <CircularProgress size={22} /> : "Submit"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* ================= EXPORT TAB ================= */}
      {activeTab === 1 && (
        <Paper sx={{ mt: 4, p: 4, backgroundColor: "white" }} elevation={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
  <InputLabel>Select Application</InputLabel>
  <Select
    value={selectedTable}
    label="Select Application"
    onChange={(e) => setSelectedTable(e.target.value)}
    disabled={
      sourceName === "Configuration" || applicationsLoading
    }
  >
    {applicationsLoading && (
      <MenuItem disabled>
        <CircularProgress size={20} />
      </MenuItem>
    )}

    {applications.map((app) => (
      <MenuItem key={app.id} value={app.module}>
        {app.module}
      </MenuItem>
    ))}
  </Select>
</FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>File Type</InputLabel>
                <Select
                  value={exportType}
                  label="File Type"
                  onChange={(e) => setExportType(e.target.value)}
                >
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CloudDownloadIcon />}
              onClick={handleExport}
              disabled={!selectedTable || !exportType}
            >
              Export Data
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Imex;
